import { useEffect } from 'react';

interface VideoModalProps {
  open: boolean;
  onClose: () => void;
  /** YouTube video ID, e.g. "tAvwT-h9YTs". Empty string renders a placeholder. */
  youtubeId?: string;
  /** Local/remote MP4 URL. Takes precedence over `youtubeId` when set. */
  videoSrc?: string;
  title?: string;
}

/**
 * Lightweight modal for the hero trailer. ESC + backdrop close.
 * The media element is only mounted while open so playback stops on
 * close. Prefers a direct `videoSrc` (<video>) over a YouTube embed.
 */
export default function VideoModal({
  open,
  onClose,
  youtubeId = '',
  videoSrc = '',
  title = 'Video',
}: VideoModalProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      className="fixed inset-0 z-50 flex items-center justify-center bg-scrim p-4"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-4xl overflow-hidden rounded-lg bg-black shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close video"
          className="absolute right-2 top-2 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-white/90 text-text shadow hover:bg-white"
        >
          ✕
        </button>
        <div className="aspect-video w-full">
          {videoSrc ? (
            <video
              key={videoSrc}
              className="h-full w-full"
              src={videoSrc}
              title={title}
              autoPlay
              controls
              playsInline
            />
          ) : youtubeId ? (
            <iframe
              className="h-full w-full"
              src={`https://www.youtube.com/embed/${youtubeId}?autoplay=1&rel=0`}
              title={title}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-sm text-white/70">
              YouTube embed — set <code className="mx-1 rounded bg-white/10 px-1">youtubeId</code> in
              <code className="mx-1 rounded bg-white/10 px-1">HeroSection.tsx</code>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
